// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  AlertTriangle, FileWarning, RefreshCw, Plus, ChevronRight, MessageSquare,
} from 'lucide-react';
import DiscrepancyModal from '../components/DiscrepancyModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ORDER = ['aberta', 'notificado_secretario', 'escalado_coc', 'comunicado_betel', 'resolvida'];
const STATUS_LABELS = {
  aberta: 'Aberta',
  notificado_secretario: 'Secretário notificado',
  escalado_coc: 'COC escalado',
  comunicado_betel: 'Betel comunicado',
  resolvida: 'Resolvida',
};
const STATUS_TONES = {
  aberta: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
  notificado_secretario: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  escalado_coc: 'bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300',
  comunicado_betel: 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-300',
  resolvida: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
};

export default function DiscrepanciesPage() {
  const { isAdmin } = useAuth();
  const isAdminUser = isAdmin();

  const [list, setList] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [transition, setTransition] = useState({ open: false, id: null, current: null });
  const [transitionNote, setTransitionNote] = useState('');
  const [transitionTarget, setTransitionTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`${API}/discrepancies${params}`);
      setList(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error('Erro ao carregar discrepâncias');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openTransition = (item) => {
    setTransition({ open: true, id: item.id, current: item.status });
    setTransitionNote('');
    const currentIdx = STATUS_ORDER.indexOf(item.status);
    setTransitionTarget(STATUS_ORDER[Math.min(currentIdx + 1, STATUS_ORDER.length - 1)]);
  };

  const submitTransition = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/discrepancies/${transition.id}/status`, {
        new_status: transitionTarget,
        note: transitionNote || null,
      });
      toast.success('Status atualizado');
      setTransition({ open: false, id: null, current: null });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="discrepancies-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground font-heading flex items-center gap-2">
              <FileWarning className="w-7 h-7 text-red-600" />
              Discrepâncias
            </h1>
            <p className="text-muted-foreground mt-1">
              Workflow de discrepâncias conforme S-27 §53/§55/§56/§61
            </p>
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos status</SelectItem>
                {STATUS_ORDER.map(s => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!denied && (
            <Button onClick={() => setCreateOpen(true)} data-testid="new-discrepancy-button">
              <Plus className="w-4 h-4 mr-2" />
              Nova
            </Button>
            )}
          </div>
        </div>

        {denied ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground" data-testid="discrepancies-denied">
            Você não tem permissão para acessar as discrepâncias. Fale com o administrador se precisar de acesso.
          </CardContent></Card>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : list.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground">
            Nenhuma discrepância registrada{statusFilter !== 'all' ? ` com status ${STATUS_LABELS[statusFilter]}` : ''}.
          </CardContent></Card>
        ) : (
          <div className="space-y-3">
            {list.map(d => (
              <Card key={d.id} data-testid={`discrepancy-card-${d.id}`} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-foreground">{d.title}</h3>
                        <Badge className={STATUS_TONES[d.status]}>{STATUS_LABELS[d.status]}</Badge>
                        {d.rule_ref && <Badge variant="outline" className="text-xs">{d.rule_ref}</Badge>}
                        {d.month && <Badge variant="outline" className="text-xs">{d.month}</Badge>}
                        {d.value_diff !== null && d.value_diff !== undefined && (
                          <Badge variant="outline" className="text-xs">R$ {Number(d.value_diff).toFixed(2)}</Badge>
                        )}
                      </div>
                      {d.description && (
                        <p className="text-sm text-muted-foreground mb-2">{d.description}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Aberta por <strong>{d.created_by_name}</strong> em {new Date(d.created_at).toLocaleString('pt-BR')}
                        {' · '}
                        {d.history.length} {d.history.length === 1 ? 'evento' : 'eventos'} no histórico
                      </p>

                      {/* History timeline */}
                      <details className="mt-3 text-xs">
                        <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                          Ver histórico
                        </summary>
                        <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-border">
                          {d.history.map((h, i) => (
                            <div key={`${h.at}-${h.status}-${i}`}>
                              <Badge variant="outline" className="mr-1 text-xs">{STATUS_LABELS[h.status] || h.status}</Badge>
                              <span className="text-muted-foreground">
                                {new Date(h.at).toLocaleString('pt-BR')} — {h.by_name}
                              </span>
                              {h.note && <p className="ml-2 italic text-muted-foreground">"{h.note}"</p>}
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                    {isAdminUser && d.status !== 'resolvida' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openTransition(d)}
                        data-testid={`advance-status-${d.id}`}
                      >
                        <ChevronRight className="w-4 h-4 mr-1" />
                        Avançar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <DiscrepancyModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={load}
      />

      <Dialog
        open={transition.open}
        onOpenChange={(o) => !o && setTransition({ open: false, id: null, current: null })}
      >
        <DialogContent className="sm:max-w-md" data-testid="transition-modal">
          <DialogHeader>
            <DialogTitle>Avançar status</DialogTitle>
            <DialogDescription>
              Status atual: <strong>{STATUS_LABELS[transition.current]}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Novo status</Label>
              <Select value={transitionTarget} onValueChange={setTransitionTarget}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_ORDER
                    .filter(s => STATUS_ORDER.indexOf(s) > STATUS_ORDER.indexOf(transition.current))
                    .map(s => (
                      <SelectItem key={s} value={s}>
                        <MessageSquare className="w-4 h-4 mr-2 inline" />
                        {STATUS_LABELS[s]}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nota (opcional)</Label>
              <Input
                value={transitionNote}
                onChange={(e) => setTransitionNote(e.target.value)}
                placeholder="Ex: Comunicado a Betel via e-mail"
                data-testid="transition-note-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransition({ open: false, id: null, current: null })}>
              Cancelar
            </Button>
            <Button onClick={submitTransition} disabled={saving} data-testid="transition-submit">
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
